'use client';

import type { LoanTerms } from '@hyphengroup/cms-components/calculatedAttributes/store';
import { useEffect, useMemo, useState } from 'react';
import type { ModulePersonalLoanCalculations } from '../cmsService/pageDataService/modulePersonalLoanCalculations';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';

export type LoanCalculationJob = {
  moduleId: string;
  productIds: string[];
  /** What this module should be showing now. */
  terms: LoanTerms;
};

export type LoanCalculations = {
  byModule: Record<string, ModulePersonalLoanCalculations>;
  /** The terms each module's figures were calculated with. */
  termsByModule: Record<string, LoanTerms>;
};

export type LoanRecalculation = LoanCalculations & { pending: boolean };

const termsKey = (terms: LoanTerms) => `${terms.amount}:${terms.tenure}`;

/**
 * Keeps loan figures in step with the terms they should be showing.
 *
 * The caller decides which modules are in play and on what terms — a page from
 * its scope plan, the apply form from the URL. This only closes the gap: it
 * refetches the modules whose figures were calculated with other terms, one
 * call per set of terms rather than one per module, and leaves what is on
 * screen alone when a call fails.
 */
export const useLoanRecalculation = ({
  jobs,
  initial,
  isDraft,
  enabled,
}: {
  jobs: LoanCalculationJob[];
  /** What the server produced, signed with the terms it used. */
  initial: LoanCalculations;
  isDraft?: boolean;
  /** False until the caller can tell which terms are wanted. */
  enabled: boolean;
}): LoanRecalculation => {
  const [state, setState] = useState<LoanCalculations>(initial);
  // The stale set whose calls last finished, answered or failed.
  const [settledKey, setSettledKey] = useState<string | null>(null);

  const stale = useMemo(() => {
    const groups = new Map<
      string,
      { terms: LoanTerms; jobs: LoanCalculationJob[] }
    >();
    for (const job of jobs) {
      const signed = state.termsByModule[job.moduleId];
      if (signed && termsKey(signed) === termsKey(job.terms)) continue;
      const key = termsKey(job.terms);
      const group = groups.get(key) ?? { terms: job.terms, jobs: [] };
      group.jobs.push(job);
      groups.set(key, group);
    }
    return [...groups.values()];
  }, [jobs, state.termsByModule]);

  const staleKey = stale
    .map(
      (group) =>
        `${termsKey(group.terms)}=${group.jobs.map((job) => job.moduleId).join(',')}`,
    )
    .join('|');

  // Derived, so a cancelled call cannot leave it set. Before `enabled` only a
  // module that has never had figures waits: one with figures keeps them.
  const pending =
    stale.length > 0 &&
    staleKey !== settledKey &&
    (enabled ||
      stale.some((group) =>
        group.jobs.some((job) => !state.termsByModule[job.moduleId]),
      ));

  useEffect(() => {
    if (!enabled || !stale.length) return;

    let cancelled = false;

    void Promise.all(
      stale.map(async (group) => {
        // The response is positional, so the id list is both what is sent and
        // what maps each result back.
        const productIds = [
          ...new Set(group.jobs.flatMap((job) => job.productIds)),
        ];
        if (!productIds.length) return;

        try {
          const response = await feApiFrontClient.calculatePersonalLoans({
            productIds,
            amount: group.terms.amount,
            tenure: group.terms.tenure,
            isDraft: !!isDraft,
          });
          if (cancelled) return;
          if (!response || response.length !== productIds.length) {
            console.error(
              '[useLoanRecalculation] recalculation answered out of shape',
              { sent: productIds.length, received: response?.length },
            );
            return;
          }

          const byProduct: ModulePersonalLoanCalculations = {};
          productIds.forEach((productId, index) => {
            const calculation = response[index];
            if (calculation) byProduct[productId] = calculation;
          });

          setState((previous) => {
            const next: LoanCalculations = {
              byModule: { ...previous.byModule },
              termsByModule: { ...previous.termsByModule },
            };
            for (const job of group.jobs) {
              next.byModule[job.moduleId] = Object.fromEntries(
                job.productIds
                  .filter((productId) => byProduct[productId])
                  .map((productId) => [productId, byProduct[productId]]),
              );
              next.termsByModule[job.moduleId] = group.terms;
            }
            return next;
          });
        } catch (error) {
          // A failed recalculation leaves what is on screen rather than
          // blanking it: stale figures beat a table of dashes.
          console.error(
            '[useLoanRecalculation] recalculation failed',
            group.terms,
            error,
          );
        }
      }),
    ).finally(() => {
      if (!cancelled) setSettledKey(staleKey);
    });

    return () => {
      cancelled = true;
    };
    // `stale` is rebuilt on every input change; its key is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, staleKey, isDraft]);

  return { ...state, pending };
};
